import React from 'react';

import { L10nDate } from '@travel/i18n';
import type { CancellationPenaltyType } from '@travel/traveler-core/types/bookingStep';
import TimeLineSection from '@travel/ui/components/TimeLineSection';
import { isNotEmptyArray } from '@travel/utils';

import TimeZoneInfo from 'components/TimeZoneInfo';

import { Translate } from 'core/translate';
import type { TimeZoneData } from 'ProviderInformation-Types';

import styles from './timeline.module.scss';

type Props = {
  className?: string;
  cancellationPenalties?: Array<{
    deadline?: string;
    deadlineDays?: number;
    deadlineTime?: string;
    percentPenalty?: number;
    timeZoneData?: TimeZoneData;
    type?: CancellationPenaltyType | string; // This is because type is used as string in Cancel Step flow
  }>;
  reservationPrice: number;
  currency: string;
  locale: string;
  isIndirectContract: boolean;
};

type FreeCancellationDateProps = {
  deadline?: string;
  locale: string;
  timeZoneData?: TimeZoneData;
  isIndirectContract: boolean;
};

type DeadlineLabelProps = {
  deadlineTime?: string;
  percentPenalty?: number;
  deadlineDays?: number;
  locale: string;
  timeZoneData?: TimeZoneData;
  isIndirectContract: boolean;
};

function FreeCancellationDate({
  deadline,
  locale,
  timeZoneData,
  isIndirectContract,
}: FreeCancellationDateProps) {
  return deadline ? (
    <>
      <L10nDate value={deadline} format="L" locale={locale} />
      &nbsp;
      <L10nDate value={deadline} format="LT" locale={locale} />
      &nbsp;
      <span data-testid="free-cancellation-timeZoneData">
        <TimeZoneInfo timeZoneData={timeZoneData} isIndirectContract={isIndirectContract} />
      </span>
    </>
  ) : null;
}

function DeadlineLabel({
  deadlineTime,
  deadlineDays,
  timeZoneData,
  isIndirectContract,
}: DeadlineLabelProps) {
  return deadlineDays === null ? (
    // always apply penalty case
    <Translate id="Cancellation_Policy_Common.Cancellation_Policy_Modal.Non-Refund_Breakdown" />
  ) : (
    // deadlineDays case
    <>
      <Translate
        id="Cancellation_Policy_Common.Cancellation_Policy_Modal.Cancellation_Policy"
        condition={() => 'status == cancellation_penalty_deadline'}
        data={{
          deadline_days: deadlineDays,
          deadline_time: deadlineTime,
        }}
      />
      &nbsp;
      <span data-testid="deadline-timeZoneData">
        <TimeZoneInfo timeZoneData={timeZoneData} isIndirectContract={isIndirectContract} />
      </span>
    </>
  );
}

function TimeLine(props: Props) {
  const { cancellationPenalties, locale, className, isIndirectContract } = props;

  const isOnlyOne = cancellationPenalties?.length === 1;
  const noShowItem = cancellationPenalties?.find(
    (item) => item.type === 'NO_SHOW_PENALTY' || item.type === 'AfterArrival',
  );

  const color = (amount?: number) =>
    amount === 0 || null ? 'free' : amount === 100 ? 'fullPenalty' : 'partialPenalty';

  return (
    <div data-testid="timeLine-wrapper" className={className}>
      {isNotEmptyArray(cancellationPenalties) &&
        cancellationPenalties?.map(
          ({ deadline, percentPenalty, deadlineDays, type, deadlineTime, timeZoneData }, index) => {
            if (type === 'NO_SHOW_PENALTY' || type === 'AfterArrival') {
              return (
                <TimeLineSection
                  data-testid="timeLine-noShowPenalty"
                  className={styles.noShow}
                  iconClassName={styles.noShowIcon}
                  deadLine={
                    <Translate id="Cancellation_Policy_Common.Cancellation_Policy_Modal.No_Show" />
                  }
                  amount={`${noShowItem?.percentPenalty}%`}
                  isOnly={false}
                  isLast={true}
                  color={'fullPenalty'}
                  key={`NO_SHOW_PENALTY`}
                />
              );
            } else if (type === 'NO_PENALTY') {
              return (
                <TimeLineSection
                  data-testid="timeLine-freeCancellation"
                  deadLine={
                    <Translate
                      id="Cancellation_Policy_Common.Cancellation_Policy_Modal.Cancellation_Policy"
                      condition={() => 'status == free_cancellation'}
                    />
                  }
                  amount={
                    <FreeCancellationDate
                      deadline={deadline}
                      locale={locale}
                      timeZoneData={timeZoneData}
                      isIndirectContract={isIndirectContract}
                    />
                  }
                  isOnly={isOnlyOne}
                  isLast={
                    noShowItem
                      ? cancellationPenalties.length - 2 === index
                      : cancellationPenalties.length - 1 === index
                  }
                  color={'free'}
                  key={`NO_PENALTY`}
                />
              );
            } else {
              return (
                <TimeLineSection
                  data-testid="timeLine-penalty"
                  deadLine={
                    <DeadlineLabel
                      deadlineTime={deadlineTime}
                      deadlineDays={deadlineDays}
                      locale={locale}
                      timeZoneData={timeZoneData}
                      isIndirectContract={isIndirectContract}
                    />
                  }
                  amount={<>{percentPenalty}%</>}
                  isOnly={isOnlyOne}
                  // we don't want to show link line to no-show item,
                  // so once cancellationPenalties include no-show,
                  // we will not calc it in isLast props, design of figma:https://www.figma.com/file/VZxsgSXe1CGg7WSAtaoU5H/Booking-Steps?node-id=5615%3A18077
                  isLast={
                    noShowItem
                      ? cancellationPenalties.length - 2 === index
                      : cancellationPenalties.length - 1 === index
                  }
                  color={color(percentPenalty)}
                  key={`${index}.${'CANCELLATION_PENALTY'}`}
                />
              );
            }
          },
        )}
    </div>
  );
}

export default TimeLine;
